/**
 * The piece page's one autosave: a pure state machine with no React in it.
 *
 * Every PATCH from the page goes through a single instance, so each one carries the newest
 * `updated_at` the server has handed back (`base_updated_at`) and only one is ever in flight.
 * - Edits merge into one pending patch and wait `delayMs` after the last change; `{immediate}`
 *   (the destination switch) sends at once.
 * - A 409 `stale` first reads the server's copy (`recover`): when no field with unsent edits changed
 *   there, the base moves to that copy and the edits are sent again; otherwise all sending stops
 *   until `reset()` (the top bar's Reload). Going stale also answers every queued `saveNow`.
 * - A 409 "this draft is busy" (a recheck or rewrite holds the row) retries every 3 s, ten times.
 * - A 409 `stale_article` (an edit draft whose live article changed: the server refuses every
 *   write to it) marks the fields failed, keeps them for the person to copy, never retries, and
 *   calls `onEditStale` so the page reloads the piece and offers to start again.
 * - Any other refusal marks the fields it carried as failed, keeps them queued and retries them
 *   once after 5 s. A field's error clears only when that field saves, so a later save of some
 *   other field does not hide it.
 */
import { ApiError } from '../../api/client'
import { describeError } from '../../lib/describeError'
import { errorCode, type PatchBody } from '../api'
import type { SaveState } from './editorTypes'

/** The typed fields. The brand is a pick, saved at once by the brand picker (BrandPicker.tsx). */
export type PieceFields = Omit<PatchBody, 'base_updated_at' | 'review_note' | 'brand_id'>
export type PieceField = keyof PieceFields

/** The words the server refuses with while another job holds the row (section_drafts._get_for_write). */
export const BUSY_DETAIL = 'this draft is busy — try again when it finishes'

export type SaveFailure = { kind: 'stale' | 'edit_stale' | 'busy' | 'error'; message: string }

/** What a refused save means: someone else's newer copy, a row held by a running job, or a refusal. */
export function classifySaveFailure(err: unknown): SaveFailure {
  const message = describeError(err)
  if (errorCode(err) === 'stale') return { kind: 'stale', message }
  if (errorCode(err) === 'stale_article') return { kind: 'edit_stale', message }
  if (err instanceof ApiError && err.status === 409 && message === BUSY_DETAIL) return { kind: 'busy', message }
  return { kind: 'error', message }
}

/** True when ISO timestamp `a` is the same instant as `b` or later. Fractions are compared to the
 *  microsecond (the server's precision), which Date.parse alone would round away. */
export function isoAtLeast(a: string, b: string): boolean {
  const split = (s: string): [number, number] => {
    const m = /\.(\d+)/.exec(s)
    const whole = Date.parse(s.replace(/\.\d+/, ''))
    const micros = m ? Number(m[1].slice(0, 6).padEnd(6, '0')) : 0
    return [whole, micros]
  }
  const [wa, ma] = split(a)
  const [wb, mb] = split(b)
  if (Number.isNaN(wb)) return true // nothing usable to compare against: the new one wins
  if (Number.isNaN(wa)) return false
  return wa !== wb ? wa > wb : ma >= mb
}

/** The unsaved values still worth showing after `sent` saved: a field typed past what was sent stays. */
export function dropSaved(local: PieceFields, sent: PieceFields): PieceFields {
  const out: PieceFields = { ...local }
  for (const k of Object.keys(sent) as PieceField[]) if (out[k] === sent[k]) delete out[k]
  return out
}

export type AutosaveSnapshot = {
  state: SaveState
  /** The message of each field whose last save was refused. */
  errors: Partial<Record<PieceField, string>>
  /** The first of those messages, for the top bar. */
  reason: string | null
  /** The field that message belongs to (Try again resends it). */
  errorField: PieceField | null
}

export type AutosaveOptions<R extends { updated_at: string }> = {
  save: (body: PieceFields & { base_updated_at?: string }) => Promise<R>
  /** The updated_at of the copy on screen. */
  base: string | null
  delayMs?: number
  busyRetryMs?: number
  busyTries?: number
  errorRetryMs?: number
  /** The field values of the copy at `base`, and how to read them from each saved copy. */
  held?: PieceFields
  fieldsOf?: (result: R) => PieceFields
  /** Called with each saved copy and the fields that saved. */
  onSaved?: (result: R, sent: PieceFields) => void
  /** Read the server's copy after a stale refusal (needs `fieldsOf`). */
  recover?: () => Promise<R>
  /** A copy `recover` read was taken: the base moved to it. `previous` is what was held before. */
  onRecovered?: (fresh: R, previous: PieceFields) => void
  /** An edit draft's article changed underneath it (409 stale_article): nothing here can save. */
  onEditStale?: () => void
  classify?: (err: unknown) => SaveFailure
}

export type SaveNowResult = { ok: true; result: unknown } | { ok: false; kind: SaveFailure['kind']; message: string }

/** What a `saveNow` waiting in line hears when the page goes stale first. */
export const STALE_MESSAGE = 'Someone else changed this piece.'

/** How many stale refusals in a row may be recovered from before the page gives up and goes stale. */
const RECOVER_TRIES = 3

export type Autosave = {
  change(patch: PieceFields, opts?: { immediate?: boolean }): void
  /** Send whatever waits, now (failed fields too). Resolves true once everything has saved. */
  flush(): Promise<boolean>
  /** Drop every unsent edit and start again from `base` (Reload, or a body the server rewrote). */
  reset(base: string | null, held?: PieceFields): void
  /** Move the base forward to a copy saved by some other call (a claim, a summary), with its fields. */
  rebase(updatedAt: string, held?: PieceFields): void
  /** The field values of the copy at the base, as the server holds them: what unsent edits were based on. */
  readonly held: PieceFields
  /** Every field with a value typed but not yet saved. */
  unsentFields(): PieceField[]
  /** The values of those fields: the one being sent, and the newest one waiting (or refused). */
  unsentValues(): { inflight: PieceFields; queued: PieceFields }
  /** Forget a field's unsent value everywhere: waiting, refused and its error (a copy replaced it). */
  discard(field: PieceField): void
  /** Save these values now, once: no debounce, no busy or error retry, nothing left queued on failure.
   *  For edits the person saves by hand (a live piece's summary). */
  saveNow(patch: PieceFields): Promise<SaveNowResult>
  /** Nothing pending, sending, failed or stale. */
  isIdle(): boolean
  /** This field has a value typed but not yet saved: waiting, being sent, or refused. */
  hasUnsent(field: PieceField): boolean
  /** Stop sending, as a stale refusal does (the page saw a newer copy it cannot take). */
  markStale(): void
  /** Resend a refused field now (Try again), or every refused field when none is named. */
  retry(field?: PieceField): void
  readonly base: string | null
  readonly state: SaveState
  getSnapshot(): AutosaveSnapshot
  subscribe(listener: () => void): () => void
  /** Hear each copy taken after a stale refusal (see `recover`), with the fields held before it. */
  onRecovered(listener: (fresh: unknown, previous: PieceFields) => void): () => void
}

const empty = (p: object) => Object.keys(p).length === 0

export function createAutosave<R extends { updated_at: string }>(opts: AutosaveOptions<R>): Autosave {
  const delayMs = opts.delayMs ?? 1500
  const busyRetryMs = opts.busyRetryMs ?? 3000
  const busyTries = opts.busyTries ?? 10
  const errorRetryMs = opts.errorRetryMs ?? 5000
  const classify = opts.classify ?? classifySaveFailure

  let base = opts.base
  let held: PieceFields = { ...(opts.held ?? {}) }
  type Explicit = { body: PieceFields; resolve: (r: SaveNowResult) => void }
  let explicit: Explicit[] = []
  let pending: PieceFields = {}
  let failed: PieceFields = {}
  let errors: Partial<Record<PieceField, string>> = {}
  let inflight: PieceFields | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let retryTimer: ReturnType<typeof setTimeout> | null = null
  let busyTimer: ReturnType<typeof setTimeout> | null = null
  let dueNow = false
  let retryDue = false
  let busyCount = 0
  let recoverTries = 0
  let stale = false
  let epoch = 0
  const listeners = new Set<() => void>()
  const recovered = new Set<(fresh: unknown, previous: PieceFields) => void>()
  let waiters: (() => void)[] = []

  const computeState = (): SaveState => {
    if (stale) return 'stale'
    if (busyTimer) return 'busy'
    if (inflight) return 'saving'
    if (!empty(errors)) return 'error'
    if (timer || dueNow || !empty(pending)) return 'pending'
    return 'saved'
  }
  const build = (): AutosaveSnapshot => {
    const first = (Object.keys(errors) as PieceField[])[0] ?? null
    return { state: computeState(), errors: { ...errors }, reason: first ? errors[first] ?? null : null, errorField: first }
  }
  let snapshot = build()

  // A busy row (a check or rewrite holds it) counts as settled for flush(): a write waiting on
  // the flush is refused at once rather than waiting out every retry.
  const settled = () => stale || !!busyTimer || (!inflight && !timer && !dueNow && !retryDue && explicit.length === 0)
  const notify = () => {
    const next = build()
    const same = next.state === snapshot.state && next.reason === snapshot.reason
      && JSON.stringify(next.errors) === JSON.stringify(snapshot.errors)
    if (!same) {
      snapshot = next
      for (const l of [...listeners]) l()
    }
    if (settled() && waiters.length) {
      const w = waiters
      waiters = []
      for (const r of w) r()
    }
  }

  /** Stop sending until reset; every hand save still waiting hears so at once. */
  const goStale = () => {
    stale = true
    for (const t of [timer, retryTimer]) if (t) clearTimeout(t)
    timer = retryTimer = null
    dueNow = retryDue = false
    const waiting = explicit
    explicit = []
    for (const x of waiting) x.resolve({ ok: false, kind: 'stale', message: STALE_MESSAGE })
    notify()
  }

  const same = (a: unknown, b: unknown) => (a ?? '') === (b ?? '')

  /** After a stale refusal: take the server's copy when no field with unsent edits changed there.
   *  A field the server already holds at our value counts as saved. */
  const takeFresh = (fresh: R): boolean => {
    if (!opts.fieldsOf) return false
    if (base && !isoAtLeast(fresh.updated_at, base)) return false
    const f = opts.fieldsOf(fresh)
    const queued: PieceFields = { ...failed, ...pending }
    const keys = Object.keys(queued) as PieceField[]
    if (keys.some((k) => !same(f[k], queued[k]) && !same(f[k], held[k]))) return false
    for (const k of keys) {
      if (same(f[k], queued[k])) { delete pending[k]; delete failed[k]; delete errors[k] }
    }
    const previous = held
    base = fresh.updated_at
    held = f
    opts.onRecovered?.(fresh, previous)
    for (const l of [...recovered]) l(fresh, previous)
    return true
  }

  const kick = () => {
    if (stale || inflight) return
    if (explicit.length) {
      const next = explicit.shift() as Explicit
      void run(next.body, true, next.resolve)
      return
    }
    if (busyTimer) return
    let body: PieceFields = {}
    let isRetry = false
    if (retryDue) {
      body = { ...failed }
      failed = {}
      retryDue = false
      isRetry = !empty(body)
    }
    if (dueNow) {
      body = { ...body, ...pending }
      pending = {}
      dueNow = false
    }
    if (empty(body)) { notify(); return }
    void run(body, isRetry)
  }

  const run = async (body: PieceFields, isRetry: boolean, done?: Explicit['resolve']) => {
    const mine = epoch
    inflight = body
    notify()
    try {
      const result = await opts.save({ ...body, ...(base ? { base_updated_at: base } : {}) })
      if (mine !== epoch) return
      inflight = null
      busyCount = 0
      recoverTries = 0
      if (!base || isoAtLeast(result.updated_at, base)) {
        base = result.updated_at
        if (opts.fieldsOf) held = opts.fieldsOf(result)
      }
      for (const k of Object.keys(body) as PieceField[]) delete errors[k]
      opts.onSaved?.(result, body)
      done?.({ ok: true, result })
    } catch (err) {
      if (mine !== epoch) return
      inflight = null
      const f = classify(err)
      if (done) {
        // A hand save fails once and says so; the person's text stays in their field.
        done({ ok: false, kind: f.kind, message: f.message })
        if (f.kind === 'stale') goStale()
        else {
          if (f.kind === 'edit_stale') opts.onEditStale?.()
          kick()
        }
        notify()
        return
      }
      if (f.kind === 'stale') {
        pending = { ...body, ...pending }
        if (opts.recover && opts.fieldsOf && recoverTries < RECOVER_TRIES) {
          recoverTries += 1
          inflight = body // still saving while the server's copy is read
          notify()
          let fresh: R | null = null
          try { fresh = await opts.recover() } catch { fresh = null }
          if (mine !== epoch) return
          inflight = null
          if (fresh && takeFresh(fresh)) {
            if (!empty(pending)) dueNow = true
            kick()
            notify()
            return
          }
        }
        goStale()
        return
      }
      if (f.kind === 'busy' && busyCount < busyTries) {
        busyCount += 1
        pending = { ...body, ...pending }
        busyTimer = setTimeout(() => { busyTimer = null; dueNow = true; kick() }, busyRetryMs)
        notify()
        return
      }
      busyCount = 0
      if (f.kind === 'edit_stale') opts.onEditStale?.()
      for (const k of Object.keys(body) as PieceField[]) {
        if (!(k in pending)) (failed as Record<string, unknown>)[k] = body[k]
        errors[k] = f.message
      }
      if (!isRetry && f.kind === 'error' && !retryTimer) {
        retryTimer = setTimeout(() => { retryTimer = null; retryDue = true; kick() }, errorRetryMs)
      }
    }
    kick()
  }

  return {
    change(patch, o) {
      for (const k of Object.keys(patch) as PieceField[]) delete failed[k]
      pending = { ...pending, ...patch }
      if (stale) { notify(); return }
      if (timer) clearTimeout(timer)
      timer = null
      if (o?.immediate) {
        dueNow = true
        kick()
      } else {
        timer = setTimeout(() => { timer = null; dueNow = true; kick() }, delayMs)
      }
      notify()
    },
    flush() {
      if (timer) clearTimeout(timer)
      timer = null
      if (!stale) {
        if (!empty(pending)) dueNow = true
        if (!empty(failed)) retryDue = true
        kick()
      }
      return new Promise<boolean>((resolve) => {
        const done = () => resolve(computeState() === 'saved')
        if (settled()) done()
        else waiters.push(done)
      })
    },
    reset(b, h) {
      epoch += 1
      for (const t of [timer, retryTimer, busyTimer]) if (t) clearTimeout(t)
      timer = retryTimer = busyTimer = null
      pending = {}
      failed = {}
      errors = {}
      inflight = null
      dueNow = retryDue = false
      busyCount = 0
      recoverTries = 0
      stale = false
      base = b
      if (h !== undefined) held = { ...h }
      for (const x of explicit) x.resolve({ ok: false, kind: 'stale', message: 'The piece was reloaded.' })
      explicit = []
      notify()
    },
    rebase(updatedAt, h) {
      if (base && !isoAtLeast(updatedAt, base)) return
      base = updatedAt
      if (h !== undefined) held = { ...h }
    },
    get held() { return held },
    unsentFields() {
      const all = new Set<PieceField>([
        ...(Object.keys(pending) as PieceField[]), ...(Object.keys(failed) as PieceField[]),
        ...(inflight ? (Object.keys(inflight) as PieceField[]) : []),
      ])
      return [...all]
    },
    unsentValues() {
      return { inflight: { ...(inflight ?? {}) }, queued: { ...failed, ...pending } }
    },
    discard(field) {
      delete pending[field]
      delete failed[field]
      delete errors[field]
      notify()
    },
    saveNow(patch) {
      for (const k of Object.keys(patch) as PieceField[]) { delete pending[k]; delete failed[k]; delete errors[k] }
      if (stale) return Promise.resolve({ ok: false as const, kind: 'stale' as const, message: STALE_MESSAGE })
      return new Promise((resolve) => {
        explicit.push({ body: { ...patch }, resolve })
        kick()
        notify()
      })
    },
    isIdle: () => !stale && !inflight && !timer && !busyTimer && !dueNow && empty(pending) && empty(failed),
    hasUnsent: (field) => field in pending || field in failed || (!!inflight && field in inflight),
    markStale() {
      if (stale) return
      goStale()
    },
    retry(field) {
      if (stale) return
      const fields = field ? [field] : (Object.keys(failed) as PieceField[])
      for (const f of fields) {
        if (!(f in failed)) continue
        if (!(f in pending)) (pending as Record<string, unknown>)[f] = failed[f]
        delete failed[f]
      }
      if (!empty(pending)) dueNow = true
      kick()
      notify()
    },
    get base() { return base },
    get state() { return snapshot.state },
    getSnapshot: () => snapshot,
    subscribe(l) {
      listeners.add(l)
      return () => { listeners.delete(l) }
    },
    onRecovered(l) {
      recovered.add(l)
      return () => { recovered.delete(l) }
    },
  }
}
